// Centralised lazy-import registry.
//
// Every route chunk is declared here exactly once and consumed by:
//   • App.js     — for `React.lazy(...)` and the eager prefetch on mount
//   • Any nav UI — for hover / touch prefetching (e.g. MobileBottomNav.js)
//
// Centralising avoids drift between App.js's `lazy()` paths and the
// navigation components' prefetch paths.

export const prefetchMap = {
  landing:           () => import("../pages/LandingPage"),
  login:             () => import("../pages/LoginPage"),
  resetPassword:     () => import("../pages/ResetPasswordPage"),
  dashboard:         () => import("../pages/DashboardPage"),
  products:          () => import("../pages/ProductsPage"),
  productForm:       () => import("../pages/ProductFormPage"),
  productDetail:     () => import("../pages/ProductDetailPage"),
  scanner:           () => import("../pages/ScannerPage"),
  stockMovement:     () => import("../pages/StockMovementPage"),
  locations:         () => import("../pages/LocationsPage"),
  reports:           () => import("../pages/ReportsPage"),
  users:             () => import("../pages/UsersPage"),
  notifications:     () => import("../pages/NotificationsPage"),
  settings:          () => import("../pages/SettingsPage"),
  search:            () => import("../pages/SearchPage"),
  userActivity:      () => import("../pages/UserActivityPage"),
  moveSummary:       () => import("../pages/MoveSummaryPage"),
  transactions:      () => import("../pages/TransactionsPage"),
  mobileMenu:        () => import("../pages/MobileMenuPage"),
  usersManagement:   () => import("../pages/UsersManagementPage"),
  activeDevices:     () => import("../pages/ActiveDevicesPage"),
  backupSettings:    () => import("../pages/BackupSettingsPage"),
  pickLists:         () => import("../pages/PickListsPage"),
  categories:        () => import("../pages/CategoriesPage"),
  audit:             () => import("../pages/AuditPage"),
  audits:            () => import("../pages/AuditsListPage"),
  auditCreate:       () => import("../pages/AuditCreatePage"),
  auditDetail:       () => import("../pages/AuditDetailPage"),
  invoicing:         () => import("../pages/InvoicingPage"),
  invoiceGenerator:  () => import("../pages/InvoiceGeneratorPage"),
  notificationsSettings: () => import("../pages/NotificationsSettingsPage"),
  suppliersManagement:   () => import("../pages/SuppliersManagementPage"),
};

// Map from a `to=` Link path to the prefetcher to call. Bottom-nav and other
// `<Link>`-based components look up the path here on hover/touch and fire the
// `import()` so the chunk arrives before the click resolves.
export const prefetchByPath = {
  "/transactions":          prefetchMap.transactions,
  "/pick-lists":            prefetchMap.pickLists,
  "/locations":             prefetchMap.locations,
  "/reports":               prefetchMap.reports,
  "/reports?tab=movements": prefetchMap.reports,
  "/mobile-menu":           prefetchMap.mobileMenu,
  "/scanner":               prefetchMap.scanner,
  "/products":              prefetchMap.products,
  "/products/new":          prefetchMap.productForm,
  "/search":                prefetchMap.search,
  "/dashboard":             prefetchMap.dashboard,
  "/notifications":         prefetchMap.notifications,
  "/settings":              prefetchMap.settings,
  "/admin/invoicing":       prefetchMap.invoicing,
  "/settings/invoice-details": prefetchMap.invoicing,
  "/invoice":               prefetchMap.invoiceGenerator,
  "/settings/backup":       prefetchMap.backupSettings,
  "/settings/notifications": prefetchMap.notificationsSettings,
  "/settings/suppliers":    prefetchMap.suppliersManagement,
  "/categories":            prefetchMap.categories,
  "/admin/users":           prefetchMap.usersManagement,
  "/admin/devices":         prefetchMap.activeDevices,
  "/users":                 prefetchMap.users,
  "/user-activity":         prefetchMap.userActivity,
  "/move-summary":          prefetchMap.moveSummary,
  "/stock-movement":        prefetchMap.stockMovement,
};

// Fire every page's `import()` once. Idempotent — repeat calls are a no-op so
// it's safe to call from multiple mount points (e.g. App.js useEffect AND a
// later `<RoutePrefetcher />` re-mount on token refresh).
let _allKicked = false;
export function prefetchAllRoutes() {
  if (_allKicked) return;
  _allKicked = true;
  for (const fn of Object.values(prefetchMap)) {
    try { fn().catch(() => {}); } catch { /* no-op */ }
  }
}

// Prefetch a single Link target by its `to=` path. Safe to call repeatedly.
export function prefetchPath(path) {
  if (!path) return;
  const fn = prefetchByPath[path];
  if (fn) {
    try { fn().catch(() => {}); } catch { /* no-op */ }
  }
}

// Eager prefetch for ProductDetail chunk — call on hover/touchstart of a
// product card so the JS is in cache the moment the user navigates.
let _productDetailKicked = false;
export function prefetchProductDetail() {
  if (_productDetailKicked) return;
  _productDetailKicked = true;
  try { prefetchMap.productDetail().catch(() => {}); } catch { /* no-op */ }
}
